import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FieldErrorsImpl, UseFormWatch } from 'react-hook-form';
import {
  ICountry,
  useGetAllCountryList,
} from './apiEndpoints/commApiEndpoints';
import { debounce } from './debounce';

interface MultiSelectProps {
  register: any;
  name: string;
  onChange?: (selectedOptions: ICountry[]) => void;
  setValue?: (name: string, value: any) => void;
  control?: any;
  label: string;
  isRequired?: boolean;
  errors: Partial<FieldErrorsImpl<any>>;
  placeholder?: string;
  watch?: UseFormWatch<any>;
}

export const CustomMultiSelect: React.FC<MultiSelectProps> = ({
  register,
  name,
  onChange,
  setValue,
  label,
  isRequired,
  errors,
  placeholder = 'Select options...',
  watch,
}) => {
  const continent_id = watch?.('continent_id');
  const [searchTerm, setSearchTerm] = useState('');
  const { data } = useGetAllCountryList({ continent_id, name: searchTerm });

  const [selectedOptions, setSelectedOptions] = useState<ICountry[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (setValue) {
      const countryIds = selectedOptions?.map((item) => item?.id);
      setValue(name, countryIds);
    }
  }, [selectedOptions, name, setValue]);
  useEffect(() => {
    setSelectedOptions([]);
    onChange?.([]);
    setSearchTerm('');
  }, [continent_id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus search input when dropdown opens
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleSelect = (option: ICountry) => {
    const isOptionSelected = selectedOptions.some(
      (item) => item.id === option.id
    );

    const updatedOptions = isOptionSelected
      ? selectedOptions.filter((item) => item.id !== option.id)
      : [...selectedOptions, option];

    setSelectedOptions(updatedOptions);
    onChange?.(updatedOptions);
    setSearchTerm(''); // Clear search after selection
  };

  const removeOption = (option: ICountry, e: React.MouseEvent) => {
    e.stopPropagation();
    const updatedOptions = selectedOptions.filter(
      (item) => item.id !== option.id
    );
    setSelectedOptions(updatedOptions);
    onChange?.(updatedOptions);
  };

  const isOptionSelected = (option: ICountry) =>
    selectedOptions.some((item) => item.id === option.id);

  const handleSearch = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
    }, 500),
    []
  );
  return (
    <div
      style={{
        maxWidth: '100%',
        margin: '0 auto',
        fontFamily: 'Arial, sans-serif',
        position: 'relative',
      }}
      ref={dropdownRef}
    >
      <label htmlFor={name}>{label}</label>
      {isRequired && <span className='require'> *</span>}

      <div
        style={{
          position: 'relative',
          border: `1px solid ${errors[name] ? 'red' : '#ccc'}`,
          borderRadius: '5px',
          padding: '5px 10px',
          minHeight: '37px',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          background: 'white',
        }}
        onClick={() => setIsDropdownOpen(true)}
      >
        {/* Selected Options Display */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '5px',
            alignItems: 'center',
            minHeight: '26px',
          }}
        >
          {selectedOptions?.map((option) => (
            <span
              key={option.id}
              style={{
                backgroundColor: 'rgb(106 112 119)',
                color: '#fff',
                padding: '2px 8px',
                borderRadius: '15px',
                fontSize: '14px',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              {option?.name}
              <button
                type='button'
                onClick={(e) => removeOption(option, e)}
                style={{
                  marginLeft: '4px',
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                ×
              </button>
            </span>
          ))}

          {/* Search input when dropdown is open */}
          {isDropdownOpen ? (
            <input
              ref={searchInputRef}
              type='text'
              onChange={(e) => handleSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder='Search...'
              style={{
                border: 'none',
                outline: 'none',
                flexGrow: 1,
                width: '50px',
              }}
            />
          ) : selectedOptions.length === 0 ? (
            <span style={{ color: '#888', fontSize: '14px' }}>
              {placeholder}
            </span>
          ) : null}
        </div>

        {/* Dropdown Options */}
        {isDropdownOpen && (
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: '5px 0 0 0',
              border: '1px solid #ccc',
              borderRadius: '5px',
              position: 'absolute',
              width: '100%',
              backgroundColor: '#fff',
              zIndex: 10,
              boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
              top: '100%',
              left: 0,
              maxHeight: '200px',
              overflowY: 'auto',
            }}
          >
            {data?.data?.length ? (
              data?.data?.map((option) => (
                <li
                  key={option.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(option);
                  }}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    backgroundColor: isOptionSelected(option)
                      ? 'rgb(202 228 255)'
                      : '#fff',
                    color: isOptionSelected(option) ? '#000' : '#000',
                    // ':hover': {
                    //   backgroundColor: '#f5f5f5',
                    // },
                  }}
                >
                  {option.name}
                  {isOptionSelected(option) && (
                    <span style={{ float: 'right', color: 'green' }}>✓</span>
                  )}
                </li>
              ))
            ) : (
              <li style={{ padding: '8px 12px', color: '#888' }}>
                No options found
              </li>
            )}
          </ul>
        )}
      </div>

      {errors[name] && (
        <span style={{ color: 'red', fontSize: '12px' }}>
          {errors[name]?.message as string}
        </span>
      )}

      <input
        type='hidden'
        {...register(name, {
          required: isRequired ? 'This field is required' : false,
          validate: (value: string[]) => {
            if (isRequired && (!value || value?.filter(Boolean).length === 0)) {
              return 'At least one option must be selected';
            }
            return true;
          },
        })}
        value={selectedOptions.join(',')}
      />
    </div>
  );
};
